import { UAContext } from "@quentin-sommer/react-useragent";
import { useNavigate } from "@remix-run/react";
import classnames from "classnames";
import React from "react";
import { useTranslation } from "react-i18next";
import { Sheet } from "react-modal-sheet";
import { useDispatch } from "react-redux";
import Wallet from "~/assets/svg/Wallet";
import Modal from "~/components/Modal";
import { setModalParams } from "~/containers/Modal";
import { postPurchaseProduct } from "~/services/api";
import ceilify from "~/utils/ceilify";
import to from "~/utils/to";

function NotEnoughBalanceAlert({
  visible,
  onClose,
  cookie,
  user,
  product,
  params,
}: {
  visible: boolean;
  onClose: () => void;
  cookie: any;
  product: any;
  params: any;
}) {
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState(null);
  const insufficientBalance = cookie?.user?.balance < product?.price;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { uaResults } = React.useContext(UAContext);
  const { t, i18n } = useTranslation();

  const onSubmit = async () => {
    if (user.risk > 7) return null;
    setError(null);
    setSubmitting(true);

    const [err] = await to(
      postPurchaseProduct({
        token: cookie.token,
        _productId: product._id,
      }),
    );

    if (err) {
      if (err.response?.data?.data?.label === "no_number") {
        return dispatch(
          setModalParams({
            target: "retryingOrder",
            props: { cookie, params },
          }),
        );
      }
      setSubmitting(false);
      return setError(err);
    }

    setError(null);
    setSubmitting(false);
    dispatch(setModalParams({ target: "" }));
    navigate("/operations");
  };

  const Content = () => {
    if (!cookie || !product || !params || !user) return null;
    return (
      <section className="bg-base-100">
        <div className="h-40 bg-[#f27f0c]/10 inline-flex justify-center items-center overflow-hidden w-full mb-4">
          <Wallet className="w-16 h-16 text-[#f27f0c]" />
        </div>
        <div className="relative flex-col gap-8 border-0 bg-base-100 pt-4 pb-[calc(env(safe-area-inset-bottom)+1rem)]">
          <div className="mb-5 flex flex-col justify-center items-center gap-2 px-4 pb-4">
            <div className="font-semibold font-['Hubot-Sans', 'IRANYekanXVF'] text-xl">
              {t("modals.notEnoughBalance.title")}
            </div>
            <div className="self-stretch text-center justify-start text-neutral-400 text-sm font-normal leading-normal">
              {t("modals.notEnoughBalance.description", {
                amount: ceilify(product.price - cookie.user.balance),
              })}
            </div>
          </div>

          <div className="flex flex-col justify-center items-center gap-4 px-4">
            <button
              onClick={async () => {
                onClose();
                navigate("/profile/charge");
              }}
              className={classnames(
                "btn btn-primary btn-block justify-center text-center",
                { disabled: submitting },
              )}
            >
              <span className="font-bold text-neutral-50">
                {t("modals.notEnoughBalance.addCredits")}
              </span>
            </button>

            <button
              onClick={onClose}
              className="btn btn-block text-neutral-900 bg-transparent border-none font-semibold font-['Figtree', 'IRANYekanXVF']"
            >
              {t("modals.notEnoughBalance.maybeLater")}
            </button>
          </div>
        </div>
      </section>
    );
  };

  if (uaResults.mobile) {
    return (
      <Sheet
        isOpen={visible}
        onClose={onClose}
        detent="content-height"
        tweenConfig={{ ease: "easeIn", duration: 0.3 }}
        disableScrollLocking
      >
        <Sheet.Container className="!rounded-t-3xl !bg-base-100 overflow-hidden">
          <Sheet.Scroller>
            <Sheet.Content>
              <Content />
            </Sheet.Content>
          </Sheet.Scroller>
        </Sheet.Container>
        <Sheet.Backdrop className="backdrop-blur-sm" onTap={onClose} />
      </Sheet>
    );
  }
  return (
    <Modal
      isOpen={visible}
      onClose={onClose}
      classNames={{ dialog: "max-w-md" }}
      closeButton={false}
    >
      <Content />
    </Modal>
  );
}

export default NotEnoughBalanceAlert;
